import { useCallback, useEffect, useRef } from 'react'
import { useRecoilCallback, useRecoilValue } from 'recoil'
import { useTranslation } from 'react-i18next'
import {
  getEditorPageContent,
  saveEditorPageContent,
} from '@/api/editorPageContent'
import {
  editorPageContentAtomFamily,
  editorSelfDeletedPageIdsAtom,
  editorTextBoxDraftsAtom,
  editorTextBoxPendingDeletesAtom,
} from '@/atoms/editorTextBoxAtoms'
import { triggerUseEffect } from '@/atoms/editorAtoms'
import { useEditorFlushTextBoxGeometry } from '@/hooks/editor/useEditorFlushTextBoxGeometry'
import { useErrorHandler } from '@/hooks/utility/useErrorHandler'
import { useSetToast } from 'hooks/utility/useSetToast'
import {
  editorTextBoxDraftsAfterConflict,
  editorTextBoxPendingDeletesAfterConflict,
  editorTextBoxPendingWrites,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import type { EditorTextBoxDocument } from '@/utils/editor-text-box/editorTextBoxAdapter'
import { isOrderPageGoneError } from '@/utils/editor/orderPageGone'
import { isEditAccessLostError } from '@/utils/editor/editAccessLost'
import { editorAccessLostPromptAtom } from '@/atoms/_editorAtoms'
import { logger, EventCategory } from '@/utils/logger'

export type EditorTextBoxSaveOutcome = {
  attempted: boolean
  saved: boolean
  conflicted: boolean
  failed: boolean
  /** A page this save wrote to is no longer on the line. */
  stale: boolean
  /** The pages that answered so, for the caller to drop drafts for. */
  stalePageIds: string[]
  /** This tab's edit link no longer reaches the order; the prompt is raised. */
  accessLost: boolean
}

const withoutKeys = <T>(
  record: Readonly<Record<string, T>>,
  keys: readonly string[],
): Record<string, T> => {
  if (keys.every((key) => !(key in record))) return record as Record<string, T>
  const next = { ...record }
  for (const key of keys) delete next[key]
  return next
}

export const useEditorSaveTextBoxes = () => {
  const { t } = useTranslation()
  const setToast = useSetToast()
  const handleError = useErrorHandler('EditorTextBoxSave')
  const flushGeometry = useEditorFlushTextBoxGeometry()
  // Atom writes only become visible to a later run after React commits, so a
  // second save in the same gesture still reads the drafts the first save
  // already cleared. This ref is the commit-timing-proof record of what each
  // box last saved, so that run re-sends nothing the server already holds.
  const lastSavedRef = useRef(new Map<string, EditorTextBoxDocument>())
  // An editor load refills every page from the server, so what this hook
  // last sent says nothing about what the rows now hold: a restore or a reset
  // between the two can leave the server holding different words. Keeping the
  // record would make the next save skip a box whose draft happens to match
  // it. This is the atom Editor.js's load effect keys on.
  const editorLoad = useRecoilValue(triggerUseEffect)
  useEffect(() => {
    lastSavedRef.current = new Map()
  }, [editorLoad])

  // Read at the moment a page answers, not from the snapshot this run
  // started with: the delete that put a page in this set can land after the
  // save went out.
  const readSelfDeletedPageIds = useRecoilCallback(
    ({ snapshot }) =>
      () =>
        snapshot.getLoadable(editorSelfDeletedPageIdsAtom).valueMaybe() ?? {},
    [],
  )

  const performSave = useRecoilCallback(
    ({ snapshot, set }) =>
      async (): Promise<EditorTextBoxSaveOutcome> => {
        // The snapshot below predates the flush's write to the drafts atom,
        // so the flush's return value has to be merged in by hand.
        const flushed = await flushGeometry()
        const [draftsBefore, pendingDeletes] = await Promise.all([
          snapshot.getPromise(editorTextBoxDraftsAtom),
          snapshot.getPromise(editorTextBoxPendingDeletesAtom),
        ])
        const drafts = { ...draftsBefore, ...flushed }

        const byPage = editorTextBoxPendingWrites({
          drafts,
          pendingDeletes,
          lastSaved: lastSavedRef.current,
        })

        if (byPage.size === 0)
          return {
            attempted: false,
            saved: true,
            conflicted: false,
            failed: false,
            stale: false,
            stalePageIds: [],
            accessLost: false,
          }

        let conflicted = false
        let failed = false
        let accessLost = false
        const stalePageIds: string[] = []
        for (const [pageId, work] of byPage) {
          try {
            await saveEditorPageContent(pageId, {
              boxes: work.boxes,
              deleteBoxIds: work.deleteBoxIds,
            })
            for (const boxId of work.draftBoxIds) {
              const document = drafts[boxId]
              if (document !== undefined)
                lastSavedRef.current.set(boxId, document)
            }
            for (const boxId of work.deleteBoxIds)
              lastSavedRef.current.delete(boxId)
            const { data: bundle } = await getEditorPageContent(pageId)
            set(editorPageContentAtomFamily(pageId), bundle)
            // Tearing a live editor down re-commits its box as a draft, so a
            // deleted box can leave one behind that the next save would send
            // as a resurrection.
            set(editorTextBoxDraftsAtom, (prev) =>
              withoutKeys(prev, [...work.draftBoxIds, ...work.deleteBoxIds]),
            )
            set(editorTextBoxPendingDeletesAtom, (prev) =>
              withoutKeys(prev, work.deleteBoxIds),
            )
            logger.info({
              message: 'editor_text_box_save_success',
              category: EventCategory.SAVE,
              data: {
                pageId,
                savedBoxIds: work.draftBoxIds,
                deletedBoxIds: work.deleteBoxIds,
              },
            })
          } catch (error) {
            const response = (
              error as { response?: { status?: number; data?: unknown } }
            )?.response
            if (isOrderPageGoneError(error)) {
              // A page this tab is deleting itself went away because the user
              // asked it to: the boxes that were on it are gone with it, and
              // nothing about the line changed behind the user's back.
              const selfDeleted = readSelfDeletedPageIds()[pageId] === true
              for (const boxId of work.draftBoxIds)
                lastSavedRef.current.delete(boxId)
              for (const boxId of work.deleteBoxIds)
                lastSavedRef.current.delete(boxId)
              if (!selfDeleted) stalePageIds.push(pageId)
              logger.info({
                message: selfDeleted
                  ? 'editor_text_box_save_page_deleted_here'
                  : 'editor_text_box_save_page_gone',
                category: EventCategory.SAVE,
                data: { pageId },
              })
            } else if (response?.status === 409) {
              conflicted = true
              logger.info({
                message: 'editor_text_box_save_conflict',
                category: EventCategory.SAVE,
                data: { pageId, conflict: response?.data ?? null },
              })
              try {
                // The server writes a page in one transaction, so a 409 means
                // nothing from this save landed.
                const { data: bundle } = await getEditorPageContent(pageId)
                set(editorPageContentAtomFamily(pageId), bundle)
                set(editorTextBoxDraftsAtom, (prev) =>
                  editorTextBoxDraftsAfterConflict(prev, pageId, bundle),
                )
                set(editorTextBoxPendingDeletesAtom, (prev) =>
                  editorTextBoxPendingDeletesAfterConflict(
                    prev,
                    pageId,
                    bundle,
                  ),
                )
                setToast({ text: t('toast.editorTextBoxConflict') })
              } catch (reloadError) {
                handleError(
                  'The page could not be reloaded after a save conflict',
                  reloadError instanceof Error
                    ? reloadError
                    : new Error(String(reloadError)),
                )
              }
            } else if (isEditAccessLostError(error)) {
              // This page was not written, and this tab's link no longer
              // reaches the order: the editor's prompt says so once for the
              // gesture.
              failed = true
              accessLost = true
              set(editorAccessLostPromptAtom, true)
              logger.info({
                message: 'editor_text_box_save_edit_access_lost',
                category: EventCategory.SAVE,
                data: { pageId },
              })
            } else {
              failed = true
              const message =
                (response?.data as { message?: string } | undefined)?.message ??
                (error instanceof Error ? error.message : String(error))
              handleError(
                message,
                error instanceof Error ? error : new Error(String(error)),
              )
            }
          }
        }
        return {
          attempted: true,
          saved: !conflicted && !failed && stalePageIds.length === 0,
          conflicted,
          failed,
          stale: stalePageIds.length > 0,
          stalePageIds,
          accessLost,
        }
      },
    [flushGeometry, handleError, setToast, t],
  )

  // One save gesture invokes this more than once (the editor's save effect
  // re-fires on page-state churn). Runs are serialized so each starts from
  // the previous run's outcome instead of racing it to the same rows.
  const queueRef = useRef<Promise<unknown>>(Promise.resolve())
  return useCallback((): Promise<EditorTextBoxSaveOutcome> => {
    const run = queueRef.current.then(
      () => performSave(),
      () => performSave(),
    )
    queueRef.current = run.catch(() => undefined)
    return run
  }, [performSave])
}

export default useEditorSaveTextBoxes
